import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../Modal';
import { ContractDocumentPreview, type ContractDocumentPreviewProps } from '../contract-wizard/ContractDocumentPreview';
import { formatKronor } from '../contract-wizard/milestonePlan';
import { newestFirst } from '../../utils/newestFirst';
import type { OngoingObjection } from '../../utils/objectionVariation';

export const OBJECTION_MAX = 4000;

export interface ReviewMessage {
  id: string;
  authorName: string;
  /** The person's own messages are shown on the right. */
  mine: boolean;
  text: string;
  createdAt: string;
}

export interface PersonContractReviewProps {
  preview: ContractDocumentPreviewProps;
  companyName: string;
  /** When the company signed (ISO); null while the company has not signed yet. */
  companySignedAt?: string | null;
  /** What the person pays in to Etappio (after ROT). */
  amountToPay: number;
  providerName: string;
  isVariation?: boolean;
  /** The open objection this ÄTA resolves; signing closes it (ClickUp 869fgj7uc). */
  resolvesObjection?: OngoingObjection | null;
  messages: ReviewMessage[];
  /** Why signing is not possible right now; null when the person can sign. */
  signBlockedReason?: string | null;
  /** The project is closed: no objection either. */
  objectionBlocked?: boolean;
  signing: boolean;
  signError?: string | null;
  onSign: () => void;
  /** Sends the objection; resolves true when it was sent. */
  onSendObjection: (comment: string) => Promise<boolean>;
  onClose: () => void;
  /** "Förhandsgranska" (ClickUp 869fmxfb7): the document alone, with "Gå
   * till signering" to the review while the person can sign. */
  startInPreview?: boolean;
  /** The person has signed: the preview offers no objection. */
  personSigned?: boolean;
}

type View = 'preview' | 'review' | 'objection' | 'sent';

const messageTime = (iso: string) =>
  new Date(iso).toLocaleString('sv-SE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

/**
 * The person's "Granska och signera avtal": the contract as a document with a
 * panel beside it (below it on phones) that explains the signing, shows the
 * conversation with the company and holds the objection form. Same look as the
 * company's step 3 in the contract wizard. Signing and sending the objection
 * are the caller's (the contract page's existing handlers).
 */
export function PersonContractReview({
  preview,
  companyName,
  companySignedAt,
  amountToPay,
  providerName,
  isVariation = false,
  resolvesObjection = null,
  messages,
  signBlockedReason,
  objectionBlocked = false,
  signing,
  signError,
  onSign,
  onSendObjection,
  onClose,
  startInPreview = false,
  personSigned = false,
}: PersonContractReviewProps) {
  const { t } = useTranslation();
  const ids = useId();
  const [view, setView] = useState<View>(startInPreview ? 'preview' : 'review');
  // "Tillbaka till avtalet" returns to where the objection was started.
  const [objectionFrom, setObjectionFrom] = useState<'preview' | 'review'>('review');
  const [reviewed, setReviewed] = useState(false);
  const [showConfirmError, setShowConfirmError] = useState(false);
  const [objection, setObjection] = useState('');
  const [objectionError, setObjectionError] = useState<'required' | 'failed' | null>(null);
  const [sendingObjection, setSendingObjection] = useState(false);
  const [showAllMessages, setShowAllMessages] = useState(false);
  const objectionRef = useRef<HTMLTextAreaElement>(null);

  // Opening the objection is an explicit choice: start typing right away.
  useEffect(() => {
    if (view === 'objection') objectionRef.current?.focus();
  }, [view]);

  const company = companyName || '—';
  const amount = formatKronor(amountToPay);
  // Newest first, like the contract history and the company's review: the
  // latest reply is on top, also when the whole thread is shown.
  const orderedMessages = useMemo(() => newestFirst(messages), [messages]);
  const latestCompanyMessage = orderedMessages.find(message => !message.mine);
  const shownMessages = showAllMessages || view === 'sent' ? orderedMessages : orderedMessages.slice(0, 1);
  const missingConfirm = showConfirmError && !reviewed;

  const sign = () => {
    if (signBlockedReason || signing) return;
    if (!reviewed) {
      setShowConfirmError(true);
      return;
    }
    onSign();
  };

  const sendObjection = async () => {
    if (!objection.trim()) {
      setObjectionError('required');
      return;
    }
    setSendingObjection(true);
    setObjectionError(null);
    const sent = await onSendObjection(objection.trim());
    setSendingObjection(false);
    if (sent) setView('sent');
    else setObjectionError('failed');
  };

  const banner = {
    preview: signBlockedReason
      ? { chip: t('contracts.personReview.banner.blockedChip'), title: signBlockedReason, text: '' }
      : { chip: t('contracts.personReview.banner.previewChip'), title: t('contracts.personReview.banner.previewTitle'), text: '' },
    review: signBlockedReason
      ? { chip: t('contracts.personReview.banner.blockedChip'), title: signBlockedReason, text: '' }
      : {
          chip: t('contracts.personReview.banner.reviewChip'),
          title: isVariation
            ? t('contracts.personReview.banner.reviewTitleVariation')
            : companySignedAt
              ? t('contracts.personReview.banner.reviewTitle', { company })
              : t('contracts.personReview.banner.reviewTitleUnsigned'),
          text: t('contracts.personReview.banner.reviewText'),
        },
    objection: {
      chip: t('contracts.personReview.banner.objectionChip'),
      title: t('contracts.personReview.banner.objectionTitle'),
      text: t('contracts.personReview.banner.objectionText', { company }),
    },
    sent: {
      chip: t('contracts.personReview.banner.sentChip'),
      title: t('contracts.personReview.banner.sentTitle', { company }),
      text: t('contracts.personReview.banner.sentText'),
    },
  }[view];

  const thread = messages.length > 0 && (
    <section className="contract-review-thread" aria-labelledby={`${ids}-messages`}>
      <div className="d-flex justify-content-between align-items-center gap-2">
        <h3 id={`${ids}-messages`} className="contract-review-thread-title">
          {t('contracts.personReview.messages.title', { count: messages.length })}
        </h3>
        {view !== 'sent' && messages.length > 1 && (
          <button
            type="button"
            className="btn btn-link contract-wizard-link"
            aria-expanded={showAllMessages}
            onClick={() => setShowAllMessages(value => !value)}
          >
            {showAllMessages
              ? t('contracts.personReview.messages.showLatest')
              : t('contracts.personReview.messages.showAll', { count: messages.length })}
          </button>
        )}
      </div>
      <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
        {shownMessages.map(message => (
          <li key={message.id} className={`contract-review-message${message.mine ? ' is-mine' : ''}`}>
            <div className="contract-review-message-meta">
              <strong>{message.mine ? t('contracts.personReview.messages.you') : message.authorName}</strong>
              <span>{messageTime(message.createdAt)}</span>
            </div>
            <p className="mb-0">{message.text}</p>
          </li>
        ))}
      </ul>
    </section>
  );

  const footer = (
    <div className="contract-wizard-footer">
      <div className="contract-wizard-footer-secondary">
        {view === 'objection' ? (
          <button type="button" className="btn btn-link contract-wizard-link-muted" onClick={() => { setView(objectionFrom); setObjectionError(null); }}>
            {t('contracts.personReview.footer.back')}
          </button>
        ) : (
          <button type="button" className="btn btn-link contract-wizard-link-muted d-none d-md-inline-block" onClick={onClose}>
            {t('contracts.personReview.footer.close')}
          </button>
        )}
      </div>
      <div className="contract-wizard-footer-primary">
        {view === 'preview' && !personSigned && (
          <button
            type="button"
            className="btn contract-wizard-btn-outline flex-fill flex-md-grow-0"
            onClick={() => { setObjectionFrom('preview'); setView('objection'); }}
            disabled={objectionBlocked}
          >
            <i className="feather-message-square me-2" aria-hidden="true"></i>
            {t('contracts.personReview.footer.objection')}
          </button>
        )}
        {view === 'preview' && !signBlockedReason && (
          <button type="button" className="btn contract-wizard-btn-cta flex-fill flex-md-grow-0" onClick={() => setView('review')}>
            {t('contracts.wizard.view.goToSigning')}
          </button>
        )}
        {view === 'review' && (
          <>
            <button
              type="button"
              className="btn contract-wizard-btn-outline flex-fill flex-md-grow-0"
              onClick={() => { setObjectionFrom('review'); setView('objection'); setShowConfirmError(false); }}
              disabled={objectionBlocked || signing}
            >
              <i className="feather-message-square me-2" aria-hidden="true"></i>
              {t('contracts.personReview.footer.objection')}
            </button>
            <button
              type="button"
              className="btn contract-wizard-btn-cta flex-fill flex-md-grow-0"
              onClick={sign}
              disabled={!!signBlockedReason || signing}
              title={t('contracts.actions.redirectNotice', { provider: providerName })}
            >
              {signing
                ? <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                : <i className="feather-lock me-2" aria-hidden="true"></i>}
              <span className="d-none d-sm-inline">{t('contracts.personReview.footer.sign')}</span>
              <span className="d-sm-none">{t('contracts.personReview.footer.signShort')}</span>
            </button>
          </>
        )}
        {view === 'objection' && (
          <button
            type="button"
            className="btn contract-wizard-btn-navy flex-fill flex-md-grow-0"
            onClick={() => void sendObjection()}
            disabled={sendingObjection}
          >
            {sendingObjection && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>}
            {t('contracts.personReview.footer.send')}
          </button>
        )}
        {view === 'sent' && (
          <button type="button" className="btn contract-wizard-btn-outline flex-fill flex-md-grow-0" onClick={onClose}>
            {t('contracts.personReview.footer.done')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      title={view === 'preview'
        ? t('contracts.personReview.titlePreview')
        : isVariation ? t('contracts.personReview.titleVariation') : t('contracts.personReview.title')}
      onClose={onClose}
      size="xl"
      fullscreenBelow="md"
      dialogClassName="contract-wizard-dialog"
      closeAriaLabel={t('contracts.personReview.closeAria')}
      subheader={
        <div className="contract-wizard-subheader">
          <p className="contract-wizard-subtitle pb-3">
            {t(isVariation ? 'contracts.personReview.subtitleVariation' : 'contracts.personReview.subtitle', {
              number: preview.contractNumber,
              agreement: preview.amendmentContract || preview.parentContract
                ? t('variationWizard.amendmentContract', { number: preview.variationNumber, contract: (preview.amendmentContract ?? preview.parentContract)!.contractNumber })
                : t('variationWizard.originVariation', { number: preview.variationNumber }),
              name: preview.contractName || '—', company,
            })}
          </p>
        </div>
      }
      footer={footer}
    >
      <div className="contract-wizard-body">
        <div className="contract-wizard-main" data-testid={`person-review-${view}`}>
          <div className={`contract-wizard-banner ${view === 'sent' ? 'is-draft' : 'is-sent'}`} role="status">
            <i className={`${view === 'sent' ? 'feather-check-circle' : 'feather-edit-3'} contract-wizard-banner-icon`} aria-hidden="true"></i>
            <div>
              <span className="contract-wizard-status-chip">{banner.chip}</span>
              <p className="contract-wizard-banner-title">{banner.title}</p>
              {banner.text && <p className="contract-wizard-banner-text">{banner.text}</p>}
            </div>
          </div>

          {signError && view === 'review' && (
            <div className="alert alert-danger" role="alert">{signError}</div>
          )}

          <div className="contract-wizard-review">
            <ContractDocumentPreview {...preview} chipLabel={t('contracts.personReview.chip')} />

            {view !== 'preview' && <aside className="contract-wizard-sign-panel" aria-labelledby={`${ids}-panel`}>
              {view === 'review' && (
                <>
                  <div>
                    <p className="contract-wizard-kicker mb-1">{t('contracts.personReview.panel.kicker')}</p>
                    <h2 id={`${ids}-panel`} className="contract-wizard-section-title">{t('contracts.personReview.panel.title')}</h2>
                  </div>
                  <ol className="contract-wizard-sign-steps contract-review-steps">
                    <li className={companySignedAt ? 'is-done' : ''}>
                      <span>
                      <strong>
                        {companySignedAt
                          ? t('contracts.personReview.panel.companySigned', { company })
                          : t('contracts.personReview.panel.companyFirst', { company })}
                      </strong>
                      {companySignedAt && (
                        <span className="d-block text-muted">{new Date(companySignedAt).toLocaleDateString('sv-SE')}</span>
                      )}
                      </span>
                    </li>
                    <li className="is-current">
                      <span>
                      <strong>{t('contracts.personReview.panel.youSign')}</strong>
                      <span className="d-block text-muted">{t('contracts.personReview.panel.youSignText', { provider: providerName })}</span>
                      </span>
                    </li>
                    {/* Nothing to pay in for a cost-neutral or lowering change. */}
                    {amountToPay > 0 && (
                      <li>
                        <span>
                          <strong>{t('contracts.personReview.panel.pay', { amount })}</strong>
                          <span className="d-block text-muted">{t('contracts.personReview.panel.payText')}</span>
                        </span>
                      </li>
                    )}
                  </ol>

                  {resolvesObjection && (
                    <div className="alert alert-warning mb-0 small" role="note" data-testid="person-review-objection-notice">
                      <strong className="d-block mb-1">
                        <i className="feather-info me-1" aria-hidden="true"></i>
                        {t('contracts.personReview.panel.objectionTitle')}
                      </strong>
                      {t('contracts.personReview.panel.objectionText', { number: resolvesObjection.milestoneNumber })}
                    </div>
                  )}

                  <div className={`contract-wizard-confirm${missingConfirm ? ' is-invalid' : ''}`}>
                    <div className="form-check contract-wizard-check mb-0">
                      <input
                        id={`${ids}-reviewed`}
                        type="checkbox"
                        className="form-check-input"
                        checked={reviewed}
                        disabled={!!signBlockedReason}
                        onChange={event => {
                          setReviewed(event.target.checked);
                          if (event.target.checked) setShowConfirmError(false);
                        }}
                        aria-invalid={missingConfirm || undefined}
                        aria-describedby={missingConfirm ? `${ids}-reviewed-error` : undefined}
                      />
                      <label className="form-check-label" htmlFor={`${ids}-reviewed`}>
                        {t('contracts.personReview.panel.confirmLabel')}
                      </label>
                    </div>
                    {missingConfirm && (
                      <p id={`${ids}-reviewed-error`} className="text-danger small mb-0 mt-2" role="alert">
                        {t('contracts.personReview.panel.confirmRequired')}
                      </p>
                    )}
                  </div>

                  {thread}

                  {!objectionBlocked && (
                    <p className="small text-muted mb-0">{t('contracts.personReview.panel.objectionHint', { company })}</p>
                  )}
                </>
              )}

              {view === 'objection' && (
                <>
                  <div>
                    <p className="contract-wizard-kicker mb-1">{t('contracts.personReview.objection.kicker')}</p>
                    <h2 id={`${ids}-panel`} className="contract-wizard-section-title">{t('contracts.personReview.objection.title')}</h2>
                  </div>
                  {latestCompanyMessage && (
                    <div className="contract-review-message">
                      <div className="contract-review-message-meta">
                        <strong>{t('contracts.personReview.messages.latestFrom', { company })}</strong>
                        <span>{messageTime(latestCompanyMessage.createdAt)}</span>
                      </div>
                      <p className="mb-0">{latestCompanyMessage.text}</p>
                    </div>
                  )}
                  <p className="mb-0">{t('contracts.personReview.objection.intro', { company })}</p>
                  <div>
                    <label htmlFor={`${ids}-objection`} className="form-label small fw-semibold">
                      {t('contracts.personReview.objection.label')} <span className="text-danger" aria-hidden="true">*</span>
                    </label>
                    <textarea
                      id={`${ids}-objection`}
                      className={`form-control${objectionError === 'required' ? ' is-invalid' : ''}`}
                      rows={6}
                      value={objection}
                      maxLength={OBJECTION_MAX}
                      required
                      placeholder={t('contracts.personReview.objection.placeholder')}
                      onChange={event => {
                        setObjection(event.target.value.slice(0, OBJECTION_MAX));
                        setObjectionError(null);
                      }}
                      aria-invalid={objectionError === 'required' || undefined}
                      aria-describedby={`${ids}-objection-help`}
                      ref={objectionRef}
                    />
                    <div id={`${ids}-objection-help`} className="d-flex justify-content-between gap-2 form-text">
                      <span className={objectionError === 'required' ? 'text-danger' : undefined} role={objectionError === 'required' ? 'alert' : undefined}>
                        {objectionError === 'required'
                          ? t('contracts.personReview.objection.required')
                          : t('contracts.personReview.objection.help', { company })}
                      </span>
                      <span>{t('contracts.wizard.assignment.counter', { count: objection.length, max: OBJECTION_MAX })}</span>
                    </div>
                  </div>
                  {objectionError === 'failed' && (
                    <div className="alert alert-danger mb-0" role="alert">{t('contracts.personReview.objection.error')}</div>
                  )}
                </>
              )}

              {view === 'sent' && (
                <>
                  <div>
                    <p className="contract-wizard-kicker mb-1">{t('contracts.personReview.objection.kicker')}</p>
                    <h2 id={`${ids}-panel`} className="contract-wizard-section-title">{t('contracts.personReview.sent.title')}</h2>
                  </div>
                  <p className="mb-0">{t('contracts.personReview.sent.text', { company })}</p>
                  {thread}
                </>
              )}
            </aside>}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default PersonContractReview;
